import React from 'react';
import { Link } from 'react-router-dom';

export default function PartnersSponsors() {
  const partnershipTypes = [
    {
      title: 'Experience Sponsorship',
      description: 'Align your brand with flagship experiences through strategic integration and activation.',
      opportunities: [
        {
          title: 'The Great West Africa Rendezvous (GWR)',
          description: 'Large-scale cultural convergence across West Africa'
        },
        {
          title: 'Boundless Europe Experience',
          description: 'Multi-country European travel for diaspora & groups'
        }
      ],
      benefits: [
        'Naming rights and title sponsorship',
        'Experience integration throughout the journey',
        'On-ground activation and physical presence',
        'Content & media exposure across channels',
        'Community engagement and social impact'
      ],
      color: 'from-primary to-orange-600',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      title: 'Destination & Tourism Partnerships',
      description: 'Collaborate with tourism boards and destination marketers to create impactful travel experiences.',
      collaborators: [
        'Tourism boards & destination marketing organisations',
        'Hospitality groups & hotel chains',
        'Airline partners',
        'Local cultural institutions'
      ],
      outcomes: [
        'Destination visibility and promotion',
        'Cultural exchange and authenticity',
        'Diaspora engagement and homecoming',
        'Sustainable tourism development',
        'Year-round destination appeal'
      ],
      color: 'from-blue-500 to-indigo-600',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      title: 'Brand & Corporate Experiences',
      description: 'Design custom travel experiences for corporate clients, brands, and organizations.',
      services: [
        'Corporate retreats & leadership offsites',
        'Team building and incentive travel',
        'Community & diaspora experiences',
        'Culture-led brand activations',
        'Product launches in destination settings'
      ],
      clients: [
        'Consumer and lifestyle brands',
        'Financial services and fintechs',
        'Telecommunications companies',
        'Technology and consulting firms',
        'Non-profit and community organizations'
      ],
      color: 'from-emerald-500 to-teal-600',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
        </svg>
      )
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Design',
      description: 'Co-create experience frameworks that align with brand objectives and audience insights.',
      details: ['Strategic brief development', 'Experience architecture', 'Audience journey mapping']
    },
    {
      number: '02',
      title: 'Integration',
      description: 'Seamlessly embed brand messaging and values throughout the travel experience.',
      details: ['Brand storytelling integration', 'Touchpoint optimization', 'Content strategy development']
    },
    {
      number: '03',
      title: 'Execution',
      description: 'Implement through certified operators with quality assurance and local expertise.',
      details: ['Operator coordination', 'Quality control systems', 'Real-time monitoring']
    },
    {
      number: '04',
      title: 'Governance & Reporting',
      description: 'Provide comprehensive analytics, insights, and impact measurement.',
      details: ['Performance metrics', 'ROI analysis', 'Stakeholder reporting']
    }
  ];

  const deliveryEcosystem = [
    {
      role: 'MGL Experiences Ltd',
      description: 'IP Owner & Experience Architect',
      responsibilities: ['Experience design', 'IP ownership', 'Brand governance', 'Partnership management']
    },
    {
      role: 'Licensed Operators',
      description: 'Regional Execution Partners',
      responsibilities: ['Ground operations', 'Guest management', 'Local compliance', 'Logistics execution']
    },
    {
      role: 'Technology Platform',
      description: 'Travefy & Operational Systems',
      responsibilities: ['Booking management', 'Communication tools', 'Analytics dashboard', 'Quality monitoring']
    }
  ];

  const targetPartners = [
    'Consumer and lifestyle brands seeking authentic travel connections',
    'Airlines and hospitality groups expanding experience offerings',
    'Financial services and fintechs targeting affluent travelers',
    'Telecommunications companies engaging diaspora communities',
    'Tourism boards and cultural institutions promoting destinations',
    'Corporate organizations planning retreats and incentive travel'
  ];

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-secondary text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-white/10"></div>
        </div>
        <div className="section-container relative">
          <div className="max-w-4xl">
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="w-3 h-3 bg-primary rounded-full"></div>
              <span className="text-sm font-medium text-primary uppercase tracking-wider">
                Strategic B2B Partnerships
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Partner With Experience Architects
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl">
              Destination-led, cultural, scalable experiences designed for strategic brand integration and audience engagement.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="btn-primary bg-white text-gray-900 hover:bg-gray-100"
              >
                Start Partnership Conversation
              </Link>
              <a
                href="mailto:partners@mglexperiences.com"
                className="btn-outline border-white text-white hover:bg-white hover:text-gray-900"
              >
                Email: partners@mglexperiences.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner With Us */}
      <section className="section-container">
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="section-title text-center">Why Partner With MGL Experiences</h2>
          <p className="section-subtitle text-center">
            We create destination experiences that deliver authentic engagement, measurable impact, and scalable results.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">IP-First Approach</h3>
              <p className="text-gray-600">
                We own and scale experience formats as intellectual property, ensuring consistency and long-term value.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Cultural Authenticity</h3>
              <p className="text-gray-600">
                Deep destination insights and local partnerships create genuine, impactful cultural experiences.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Scalable Execution</h3>
              <p className="text-gray-600">
                Certified operator network ensures quality delivery across multiple markets and seasons.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Opportunities */}
      <section className="bg-white py-20">
        <div className="section-container">
          <h2 className="section-title text-center">Partnership Opportunities</h2>
          <p className="section-subtitle text-center">
            Multiple pathways to align with our destination experience IP.
          </p>

          <div className="space-y-12 mt-12">
            {partnershipTypes.map((type, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${type.color}`}></div>
                <div className="p-8 md:p-12">
                  <div className="flex items-start mb-6">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${type.color} text-white flex items-center justify-center mr-4 flex-shrink-0`}>
                      {type.icon}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{type.title}</h3>
                      <p className="text-gray-600">{type.description}</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Left Column */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        {type.opportunities ? 'Flagship Experiences' : 
                         type.collaborators ? 'We Collaborate With' : 
                         'Services Include'}
                      </h4>
                      <ul className="space-y-3">
                        {(type.opportunities || type.collaborators || type.services)?.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <div className={`w-2 h-2 bg-gradient-to-r ${type.color} rounded-full mt-2 mr-3 flex-shrink-0`}></div>
                            <span className="text-gray-700">
                              {typeof item === 'string' ? item : (
                                <>
                                  <strong>{item.title}:</strong> {item.description}
                                </>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Right Column */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        {type.benefits ? 'Sponsorship Benefits' : 
                         type.outcomes ? 'Expected Outcomes' : 
                         'Target Clients'}
                      </h4>
                      <ul className="space-y-3">
                        {(type.benefits || type.outcomes || type.clients)?.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <div className={`w-2 h-2 bg-gradient-to-r ${type.color} rounded-full mt-2 mr-3 flex-shrink-0`}></div>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="section-container">
        <h2 className="section-title text-center">How We Work With Partners</h2>
        <p className="section-subtitle text-center">
          A structured 4-step process ensuring strategic alignment and measurable results.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
          {processSteps.map((step, index) => (
            <div key={index} className="relative">
              <div className="card h-full">
                <div className="text-3xl font-bold text-primary mb-4">{step.number}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600 mb-6">{step.description}</p>
                <ul className="space-y-2">
                  {step.details.map((detail, idx) => (
                    <li key={idx} className="text-sm text-gray-500 flex items-start">
                      <div className="w-1 h-1 bg-primary rounded-full mt-2 mr-2"></div>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
              {index < processSteps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gray-300"></div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Delivery Model */}
      <section className="bg-gray-900 text-white py-20">
        <div className="section-container">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Delivery Ecosystem</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {deliveryEcosystem.map((entity, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
                <h3 className="text-xl font-bold mb-2">{entity.role}</h3>
                <p className="text-gray-300 mb-6">{entity.description}</p>
                <ul className="space-y-3">
                  {entity.responsibilities.map((resp, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-300">
                      <svg className="w-4 h-4 text-primary mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {resp}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Ecosystem Flow */}
          <div className="mt-12 p-8 bg-white/5 rounded-xl">
            <div className="text-center mb-6">
              <h4 className="text-xl font-bold mb-2">Integrated Partnership Flow</h4>
              <p className="text-gray-300">Clear roles ensuring seamless experience delivery</p>
            </div>
            <div className="hidden md:flex items-center justify-between relative">
              {deliveryEcosystem.map((entity, index) => (
                <div key={index} className="text-center z-10">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold">{index + 1}</span>
                  </div>
                  <h5 className="font-semibold">{entity.role.split(' ')[0]}</h5>
                  <p className="text-sm text-gray-300">{entity.role.split(' ').slice(1).join(' ')}</p>
                </div>
              ))}
              <div className="absolute top-8 left-1/4 right-1/4 h-0.5 bg-gray-600"></div>
              <div className="absolute top-8 left-3/4 right-1/4 h-0.5 bg-gray-600"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="section-container">
        <div className="max-w-4xl mx-auto">
          <h2 className="section-title text-center">Who We Work With</h2>
          <p className="section-subtitle text-center">
            If your brand wants to connect authentically with traveling communities, we should talk.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {targetPartners.map((partner, index) => (
              <div key={index} className="flex items-start p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span className="text-gray-700">{partner}</span>
              </div>
            ))}
          </div>

          {/* Final CTA */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 md:p-12 text-white">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Start a Partnership Conversation
              </h3>
              <p className="text-xl mb-6 opacity-90">
                United Kingdom | Operating globally through certified partners
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="mailto:partners@mglexperiences.com"
                  className="bg-white text-gray-900 font-medium py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  partners@mglexperiences.com
                </a>
                <Link
                  to="/contact"
                  className="border-2 border-white text-white font-medium py-3 px-8 rounded-lg hover:bg-white hover:text-gray-900 transition-colors"
                >
                  Contact Form
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}